import { Star, BadgeCheck } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { cn } from "@/lib/utils";

type ZhRegion = "sacramento" | "la" | "san-francisco" | "san-jose" | "san-diego" | "bay-area";

const TESTIMONIALS: Record<ZhRegion, { name: string; city: string; quote: string }[]> = {
  sacramento: [
    { name: "王丽华", city: "沙加缅度，加利福尼亚", quote: "非常棒的服务！团队准时到达，把我的家打扫得一尘不染。强烈推荐！" },
    { name: "张明远", city: "罗斯维尔，加利福尼亚", quote: "深度清洁超出了我的所有期望。非常专业，注重细节。" },
    { name: "李秀梅", city: "艾尔克格罗夫，加利福尼亚", quote: "有两个孩子，我需要可靠的清洁团队。Uplevel Cleaning真的很棒！" },
  ],
  la: [
    { name: "陈美玲", city: "洛杉矶，加利福尼亚", quote: "洛杉矶最好的清洁服务！价格透明，团队专业，对结果非常满意。" },
    { name: "刘建国", city: "圣塔莫尼卡，加利福尼亚", quote: "我的海景公寓从未如此洁净。每分钱都值得！" },
    { name: "黄晓燕", city: "帕萨迪纳，加利福尼亚", quote: "一分钟内完成在线预约，家里焕然一新。南加州最佳选择！" },
  ],
  "san-francisco": [
    { name: "吴志远", city: "旧金山，加利福尼亚", quote: "在SF很难找到时间打扫卫生。Uplevel解决了这个问题——快速、优质、价格公道。" },
    { name: "郑丽娟", city: "戴利城，加利福尼亚", quote: "售房前的专业清洁服务。买家对房屋状况非常满意！" },
    { name: "赵国强", city: "伯灵格姆，加利福尼亚", quote: "服务非常满意。团队认真、细心、守时。" },
  ],
  "san-jose": [
    { name: "林德明", city: "圣何塞，加利福尼亚", quote: "作为硅谷忙碌的工程师，我需要可靠的服务。Uplevel每次都不让我失望！" },
    { name: "徐雅芳", city: "桑尼维尔，加利福尼亚", quote: "装修后的清洁工作完美无瑕。灰尘全部消失，家里闪闪发光。" },
    { name: "方剑锋", city: "帕洛阿尔托，加利福尼亚", quote: "价格公道，在线预约方便，效果出色。强烈推荐给湾区所有朋友！" },
  ],
  "san-diego": [
    { name: "何美华", city: "圣地亚哥，加利福尼亚", quote: "圣地亚哥最好的清洁服务！团队专业，每次清洁后家里都焕然一新。" },
    { name: "杨志强", city: "卡尔斯巴德，加利福尼亚", quote: "预约快捷，价格透明，质量有保证。完全符合我的需求！" },
    { name: "蔡淑芬", city: "恩西尼塔斯，加利福尼亚", quote: "住在海边，到处都是沙子。Uplevel每次都清洁得干干净净。太棒了！" },
  ],
  "bay-area": [
    { name: "潘晓明", city: "沃尔纳特克里克，加利福尼亚", quote: "Contra Costa County顶级服务。团队专业守时，清洁效果令人满意。" },
    { name: "孙芸芸", city: "弗里蒙特，加利福尼亚", quote: "搬家后的清洁服务无可挑剔。成功拿回了全额押金！" },
    { name: "钱大伟", city: "都柏林，加利福尼亚", quote: "Tri-Valley最佳选择。价格实惠，质量超出预期。" },
  ],
};

export const ChineseTestimonials = ({ region }: { region: ZhRegion }) => {
  const { ref, isVisible } = useScrollAnimation();
  const reviews = TESTIMONIALS[region] || TESTIMONIALS.sacramento;

  return (
    <section className="py-24 bg-muted/30">
      <div className="container">
        <div ref={ref} className={cn("text-center mb-16 anim-fade-up", isVisible && "is-visible")}>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">客户评价</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            来自您所在地区真实客户的真实反馈。
          </p>
        </div>
<div className={cn("anim-fade-up", isVisible && "is-visible")}>
          <div className="md:hidden">
            <div className="flex overflow-x-auto pb-8 -mx-4 px-4 snap-x snap-mandatory hide-scrollbar gap-4">
              {reviews.map((review, i) => (
                <div key={i} className="bg-background p-6 rounded-2xl shadow-sm border flex flex-col gap-4 min-w-[85vw] snap-center shrink-0">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-current" />)}
                  </div>
                  <p className="text-muted-foreground italic leading-relaxed text-sm">"{review.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-sm">{review.name}</span>
                        <BadgeCheck className="h-4 w-4 text-primary" />
                      </div>
                      <p className="text-xs text-muted-foreground">{review.city}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {reviews.map((review, i) => (
              <div key={i} className="bg-background p-8 rounded-2xl border shadow-sm hover:shadow-lg transition-all" style={{ transitionDelay: `${i * 100}ms` }}>
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, j) => <Star key={j} className="h-5 w-5 fill-current" />)}
                </div>
                <p className="text-muted-foreground mb-6 italic leading-relaxed">"{review.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                    {review.name.charAt(0)}
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="font-bold text-sm">{review.name}</span>
                      <BadgeCheck className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-xs text-muted-foreground">{review.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
